import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MiniKit } from '@worldcoin/minikit-js';
import App from './App.tsx';
import { relayCentralSsoPopupCallback } from './lib/centralSso';
import { authConfig } from './lib/config';
import './index.css';
import './i18n';
import i18n from './i18n';
import { debug } from './utils/DebugLogger';

const hasRelayedCentralSsoPopupCallback = relayCentralSsoPopupCallback();

if (!hasRelayedCentralSsoPopupCallback && import.meta.env.VITE_SUPPRESS_LOGS === 'true') {
  const noop = () => {};
  console.log = noop;
  console.info = noop;
  console.warn = noop;
  console.debug = noop;
  console.table = noop;
  console.time = noop;
  console.timeEnd = noop;
}

if (!hasRelayedCentralSsoPopupCallback) {
// 檢查環境變數；只記錄 presence，不輸出 App ID 或其他設定值。
console.log('🔍 [Main] Environment check:', {
  worldAppIdConfigured: Boolean(authConfig.worldAppId),
  nodeEnv: import.meta.env.NODE_ENV,
  dev: import.meta.env.DEV
});

// 初始化 MiniKit - 必須在應用程式啟動的最早階段完成
try {
  MiniKit.install(authConfig.worldAppId || undefined);
  
  // 詳細檢查 MiniKit 初始化狀態和配置
  console.log('🔍 [Main] MiniKit initialization details:', {
    isInstalled: MiniKit.isInstalled(),
    environment: import.meta.env.NODE_ENV,
  });
  
  // 確認 APP ID 一致性
  const miniKitAppId = MiniKit.appId;
  const envAppId = import.meta.env.VITE_WORLD_APP_ID;
  
  if (miniKitAppId && envAppId && miniKitAppId !== envAppId) {
    console.warn('⚠️ [Main] APP ID 不一致警告:', {
      miniKitAppIdConfigured: Boolean(miniKitAppId),
      envAppIdConfigured: Boolean(envAppId),
      match: miniKitAppId === envAppId
    });
    debug.warning('MiniKit', 'APP ID mismatch detected');
  } else {
    console.log('✅ [Main] APP ID consistency check completed');
  }
  
  debug.success('MiniKit', 'MiniKit initialized with App ID');
  console.log('✅ [Main] MiniKit.install() completed successfully');
} catch (error) {
  debug.error('MiniKit', 'Error initializing MiniKit:', error);
  console.error('❌ [Main] MiniKit.install() failed:', error);
}

// 初始化 Eruda 調試工具（僅在開發模式和特定條件下）
const initEruda = async () => {
  try {
    const url = new URL(window.location.href);
    const enableByQuery = url.searchParams.get('debug') === 'true';
    const enableByStorage = localStorage.getItem('eruda-debug') === 'enabled';

    // 建議：開發模式才允許，避免正式線上也被打開
    const isDev = import.meta.env.DEV;

    const shouldEnableEruda = isDev && (enableByQuery || enableByStorage);

    if (shouldEnableEruda) {
      const { default: eruda } = await import('eruda');
      eruda.init();

      // 「預設關閉」：初始化後不要自動開啟
      // 1) 不要呼叫 eruda.show()
      // 2) 確保一開始是隱藏（就算某些外部腳本曾呼叫 show）
      eruda.hide?.();

      // 可選：把入口按鈕位置往上，避免被底部 UI 遮住
      const erudaWindow = window as Window & {
        eruda?: { util?: { evalCss?: (css: string) => void } };
      };
      erudaWindow.eruda?.util?.evalCss?.(`
        .eruda-entry { bottom: 96px !important; right: 16px !important; z-index: 2147483647 !important; }
        .eruda { z-index: 2147483647 !important; }
      `);

      debug.success('Eruda', 'Eruda 初始化（預設關閉）');
      console.log('🛠️ Eruda initialized (hidden by default)');
    } else {
      // 如果真的想要完全保證預設關閉，也可以主動把舊的 storage 清掉
      // localStorage.removeItem('eruda-debug');
      debug.info('Eruda', '未啟用（需要 ?debug=true 或 localStorage=enabled，且 DEV 模式）');
    }
  } catch (error) {
    debug.error('Eruda', 'Eruda 初始化失敗', error);
    console.error('❌ Failed to initialize Eruda:', error);
  }
};

debug.info('Main', 'Application starting...');

// 初始化 Eruda
initEruda();

// 等待 i18n 完全載入後再渲染應用程式
const renderApp = () => {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>
  );
};

// 如果 i18n 已準備好，直接渲染
if (i18n.isInitialized) {
  debug.info('Main', 'i18n is already initialized, rendering app immediately');
  renderApp();
} else {
  // 否則等待 i18n 完全載入
  debug.info('Main', 'Waiting for i18n to initialize...');
  i18n.on('initialized', () => {
    debug.success('Main', 'i18n initialized, rendering app');
    renderApp();
  });
  
  // 添加超時保護，防止卡住（5秒後強制渲染）
  setTimeout(() => {
    if (!i18n.isInitialized) {
      debug.warning('Main', 'i18n initialization timeout, rendering app anyway');
      renderApp();
    }
  }, 5000);
}
}
